/**
 * Client-side image-with-price renderer.
 *
 * Loads a remote image into an HTMLCanvasElement, optionally stamps a
 * sticker-style price tag in the bottom-right corner (Instagram aesthetic),
 * and triggers a download. Designed for admins / vendors who want to
 * post product photos straight to social media with the price baked in.
 *
 * Why client-side: zero server cost, instant, no LLM credits used. The
 * image quality is identical to the source because we just composite on top.
 */
import { resolveImageUrl } from './imageUtils';

/**
 * Load an image as a same-origin <canvas>-drawable bitmap.
 * Uses `crossOrigin = 'anonymous'` so the canvas isn't tainted by CORS.
 */
const loadImage = (url) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Could not load image (CORS or 404).'));
    img.src = resolveImageUrl(url);
  });

/**
 * Draws a sticker-style price tag in the bottom-right corner of the canvas.
 * Style: rounded pill with vibrant gradient + white text + soft drop shadow.
 * Sized proportionally to the image so it looks consistent at any resolution.
 */
const drawPriceSticker = (ctx, w, h, price) => {
  const label = `₹${price}`;
  // ~9% of the shorter image dimension — big enough to read on Instagram
  // but doesn't dominate the photo.
  const fontSize = Math.round(Math.min(w, h) * 0.09);
  ctx.font = `900 ${fontSize}px "Helvetica Neue", "Helvetica", Arial, sans-serif`;
  const textWidth = ctx.measureText(label).width;
  const padX = Math.round(fontSize * 0.7);
  const padY = Math.round(fontSize * 0.45);
  const tagW = textWidth + padX * 2;
  const tagH = fontSize + padY * 2;
  const radius = tagH / 2;
  const margin = Math.round(Math.min(w, h) * 0.04);
  const x = w - tagW - margin;
  const y = h - tagH - margin;

  // Soft drop shadow so the sticker reads as a separate layer.
  ctx.save();
  ctx.shadowColor = 'rgba(0, 0, 0, 0.35)';
  ctx.shadowBlur = Math.round(fontSize * 0.4);
  ctx.shadowOffsetY = Math.round(fontSize * 0.1);

  // Gradient background — orange→pink, matches the "Get it now" CTA in-app
  // so brand language carries over to social media posts.
  const grad = ctx.createLinearGradient(x, y, x + tagW, y + tagH);
  grad.addColorStop(0, '#f59e0b');
  grad.addColorStop(0.5, '#f97316');
  grad.addColorStop(1, '#ec4899');
  ctx.fillStyle = grad;
  roundedRect(ctx, x, y, tagW, tagH, radius);
  ctx.fill();
  ctx.restore();

  // White text on top, no shadow
  ctx.save();
  ctx.fillStyle = '#ffffff';
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'center';
  ctx.font = `900 ${fontSize}px "Helvetica Neue", "Helvetica", Arial, sans-serif`;
  ctx.fillText(label, x + tagW / 2, y + tagH / 2 + Math.round(fontSize * 0.04));
  ctx.restore();
};

const roundedRect = (ctx, x, y, w, h, r) => {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
};

/**
 * Render `imageUrl` to a JPEG blob, optionally with a price sticker.
 * Returns `{ blob, filename }` ready to drop into a downloader.
 */
export async function renderImageBlob({ imageUrl, withPrice, price, baseName, index }) {
  const img = await loadImage(imageUrl);
  const canvas = document.createElement('canvas');
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0);
  if (withPrice && price && price > 0) {
    drawPriceSticker(ctx, canvas.width, canvas.height, price);
  }
  const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.95));
  const safeName = (baseName || 'product').replace(/[^a-z0-9_-]+/gi, '_').slice(0, 60);
  const priceSuffix = withPrice && price ? `_₹${price}` : '';
  const filename = `${safeName}${priceSuffix}_${index + 1}.jpg`;
  return { blob, filename };
}

/**
 * Trigger a same-tab download for a single blob.
 */
export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    URL.revokeObjectURL(url);
    a.remove();
  }, 100);
}

/**
 * Download a batch of images one by one with a small delay so mobile
 * browsers don't drop subsequent downloads.
 */
export async function downloadImagesWithOptionalPrice({ imageUrls, withPrice, price, baseName, onProgress }) {
  let success = 0;
  for (let i = 0; i < imageUrls.length; i += 1) {
    try {
      // eslint-disable-next-line no-await-in-loop
      const { blob, filename } = await renderImageBlob({
        imageUrl: imageUrls[i],
        withPrice,
        price,
        baseName,
        index: i,
      });
      downloadBlob(blob, filename);
      success += 1;
      if (onProgress) onProgress(success, imageUrls.length);
      // Small gap between downloads — some mobile browsers debounce.
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, 350));
    } catch (e) {
      // Continue with the rest — partial success is better than blocking
      // the whole batch on a single CORS failure.
      // eslint-disable-next-line no-console
      console.error('Failed to render image', imageUrls[i], e);
    }
  }
  return success;
}
